import type { Constructor } from './constructor'
import { CSSResultGroup, CSSResultOrNative, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import type { TSurfaceColor, TSurfaceOpacity } from '@schmancy/types/surface'
import { surfaceStyles } from '@schmancy/primitives/surface/surface.styles'

export declare class ISurfaceMixin {
	type: TSurfaceColor
	opacity?: TSurfaceOpacity
}

/**
 * SurfaceMixin — injects surface color-variant styles into any LitElement.
 *
 * Uses Lit's `finalizeStyles` hook to inject `surfaceTypeStyles` CSS for all
 * color-variant selectors. It carries no corner: each mixing component owns its
 * own, since they disagree — `<schmancy-surface>` defaults its painted panes to
 * the `2xl` rung, `<schmancy-details>` sits on `xl`, `<schmancy-expand-root>`
 * corners its portal panel. Shadow depth and cursor behavior stay the
 * consumer's, as stock Tailwind classes on the element (`shadow-lg`,
 * `cursor-pointer`).
 *
 * Provides two reflected properties:
 * - `type`: TSurfaceColor — the color variant (17: 8 tonal + 7 semantic + 2 utility)
 * - `opacity`: TSurfaceOpacity — liquid-glass fill density for tonal types
 *   (`glass` | `translucent` | `solid`); unset renders the
 *   `translucent` default. Ignored by semantic and utility types.
 *
 * Usage:
 * ```typescript
 * import { SurfaceMixin } from '#mixins/surface.mixin'
 *
 * @customElement('my-component')
 * class MyComponent extends SurfaceMixin(TailwindElement(css`
 *   :host { display: block; }
 * `)) {
 *   // Component now has the type property and surfaceTypeStyles
 * }
 * ```
 */
export const SurfaceMixin = <T extends Constructor<LitElement>>(superClass: T) => {
	class SurfaceMixinClass extends superClass {
		/**
		 * Override finalizeStyles to inject surfaceStyles.
		 * This is the Lit-recommended way for mixins to add styles.
		 */
		protected static finalizeStyles(styles?: CSSResultGroup): CSSResultOrNative[] {
			// Get parent's finalized styles using LitElement's method
			const parentStyles = (superClass as unknown as typeof LitElement).finalizeStyles(styles)
			// Append surfaceStyles at the end (higher specificity)
			return [...parentStyles, surfaceStyles as CSSResultOrNative]
		}

		/**
		 * Specifies the surface type for styling.
		 * @default 'containerLow'
		 */
		@property({ reflect: true })
		type: TSurfaceColor = 'containerLow'

		/**
		 * Liquid-glass fill density for tonal surfaces. Left unset, the surface
		 * renders the `translucent` default; set it to go more see-through
		 * (`glass`) or denser (`solid`). Tonal types only.
		 */
		@property({ reflect: true })
		opacity?: TSurfaceOpacity
	}
	return SurfaceMixinClass as Constructor<ISurfaceMixin> & T
}
